import * as React from 'react';
import { useState } from 'react';

import cn from 'classnames';
import { useBeforeunload } from 'react-beforeunload';
import { Link } from 'react-router-dom';
import { useCoAgent, useCopilotChat } from '@copilotkit/react-core';
import { Role, TextMessage } from '@copilotkit/runtime-client-gql';

import { Button, Text } from '@components/common';
import { WindowLayout, WindowLayoutBody } from '@components/layouts/WindowLayout';
import { HamburgerSVG } from '@components/svgs';
import { useDeviceType } from '@hooks/useDeviceType';
import { useFixedSidebar } from '@hooks/useSidebar';

import { PublishSlideout } from './agents/main/components/PublishSlideout';
import { Display } from './agents/main/Display';
import { MainAgentState } from './agents/main/types';
import { isGenerationNode } from './agents/main/utils';
import { ChatInterface } from './components/ChatInterface';
import { LandingPage } from './components/LandingPage';

interface StudioPageProps {
  agentName: 'main';
  token: string;
}

export const StudioPage = ({ agentName, token }: StudioPageProps) => {
  const {
    visibleMessages,
    appendMessage,
    setMessages,
    deleteMessage,
    reloadMessages,
    stopGeneration,
    reset,
    isLoading
  } = useCopilotChat();
  const { state, setState, nodeName } = useCoAgent<MainAgentState>({
    name: agentName,
    configurable: { gqApiToken: token }
  });

  const { toggle, open } = useFixedSidebar();
  const hasMessages = visibleMessages.length >= 2;
  const [showPublishSlideout, setShowPublishSlideout] = useState(false);
  const { isMobile } = useDeviceType();
  const [isPublished, setIsPublished] = useState(false);

  const showLoading = isLoading || (!!nodeName && isGenerationNode(nodeName));

  useBeforeunload((e) => {
    if (hasMessages) {
      e.preventDefault();
    }
  });

  const handleRecruitClick = () => {
    setShowPublishSlideout(true);
  };

  const handleCloseSlideout = () => {
    setShowPublishSlideout(false);
  };

  const handlePublish = () => {
    setIsPublished(true);
    setShowPublishSlideout(false);
  };

  const sendMessage = (content: string) => {
    appendMessage(
      new TextMessage({
        content,
        role: Role.User
      })
    );
  };

  return (
    <WindowLayout>
      <WindowLayoutBody className='flex h-screen flex-col overflow-hidden'>
        <div className='tablet:flex-row tablet:space-y-0 tablet:justify-between flex w-full flex-col items-center space-y-4 border-b border-gray-200 bg-white px-6 py-2.5'>
          <div className='mobile:flex-nowrap flex w-full flex-1 flex-wrap items-center space-x-4 self-center overflow-hidden truncate text-sm'>
            <button
              name='Toggle sidebar'
              aria-expanded={open}
              aria-label='Toggle sidebar'
              className='text-gray-500 hover:text-gray-500 hover:underline'
              onClick={toggle}
            >
              <HamburgerSVG className='text-indigo-600' />
            </button>
            <Text h='400' color='gray-500'>
              /
            </Text>
            <Link className='h400 text-gray-500 hover:text-gray-500 hover:underline' to='/studies'>
              Studies
            </Link>
            <Text h='400' color='gray-500'>
              /
            </Text>
            <Text h='400' color='gray-500'>
              {state.study?.title || 'New study'}
            </Text>
          </div>
          {!!state.study?.id && (
            <div className='flex items-center space-x-2'>
              <Button medium icon='eye' href={`/studies/${state.study.id}/preview/survey`} target='_blank'>
                Preview
              </Button>
              {!isPublished && (
                <Button disabled={showLoading} icon='recruitment' primary medium onClick={handleRecruitClick}>
                  Recruit
                </Button>
              )}
            </div>
          )}
        </div>
        <div className='flex flex-1 overflow-hidden'>
          <div
            className={cn('h-full min-h-0 flex-1 overflow-hidden transition-all duration-300 ease-in-out', {
              'mx-auto w-full rounded-lg border border-gray-50': !hasMessages,
              'w-1/4': hasMessages
            })}
          >
            <div
              className={cn('flex h-full flex-col overflow-hidden', {
                'rounded-lg': !hasMessages
              })}
            >
              {!hasMessages ? (
                <LandingPage
                  state={state}
                  setState={setState}
                  onSendMessage={sendMessage}
                  onAddPrototype={() => {}}
                  isMobile={isMobile}
                />
              ) : (
                <ChatInterface
                  messages={visibleMessages}
                  isLoading={isLoading}
                  onSendMessage={sendMessage}
                  nodeName={nodeName}
                  state={state}
                />
              )}
            </div>
          </div>
          <div
            className={cn('h-full overflow-hidden transition-all duration-300 ease-in-out', {
              'w-0 opacity-0': !hasMessages,
              'w-3/4 opacity-100': hasMessages
            })}
          >
            {hasMessages && (
              <div className='h-full overflow-auto bg-white'>
                <Display
                  state={state}
                  setState={setState}
                  isPublished={isPublished}
                  isLoading={showLoading}
                  nodeName={nodeName}
                />
              </div>
            )}
          </div>
        </div>
      </WindowLayoutBody>
      {showPublishSlideout && (
        <PublishSlideout onClose={handleCloseSlideout} onPublish={handlePublish} study={state.study} />
      )}
    </WindowLayout>
  );
};
